import { Inject, Injectable, OnDestroy } from '@angular/core'
import { Subject, skipWhile, takeUntil } from 'rxjs'
import { MapService } from 'src/app/core/services/map.service'
import { AppStateService } from 'src/app/core/services/app-state.service'
import {
  FillLayerSpecification,
  LineLayerSpecification,
  GeoJSONSource,
  MapGeoJSONFeature,
} from 'maplibre-gl'
import { FeatureCollection, Polygon } from 'geojson'
import { emptyGeoJsonSource } from 'src/app/core/utils/source-utils'
import { DataBlacklistAreaService } from '../services/data-blacklist-area.service'
import { Layers } from '../layers-config/layers'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { NavigationStart, Router } from '@angular/router'

@Injectable()
export class LayerBlacklistAreaService implements OnDestroy {
  public isLayerVisible = false
  private cleanUp$ = new Subject<void>()
  public readonly layerId = this.layer
  public readonly layerLineId = `${this.layer}-line-layer`
  public readonly sourceId = `${this.layer}-src`

  private blacklistAreaLayerSpecification: FillLayerSpecification = {
    id: this.layerId,
    type: 'fill',
    source: this.sourceId,
    paint: {
      'fill-color': '#e46666',
      'fill-opacity': 0.2,
    },
  }

  private blacklistAreaLineLayerSpecification: LineLayerSpecification = {
    id: this.layerLineId,
    type: 'line',
    source: this.sourceId,
    paint: {
      'line-color': '#ff0000',
      'line-width': 2,
      'line-opacity': 0.8,
    },
  }

  constructor(
    private router: Router,
    private mapService: MapService,
    private readonly mapEventService: MapEventsService,
    private appState: AppStateService,
    @Inject(String) private layer: string,
    private blacklistAreaService: DataBlacklistAreaService,
  ) {
    if (this.mapService.isMapReady) {
      this.initializeLayers()
    } else {
      this.mapService.mapReadyChanged.subscribe(() => {
        this.initializeLayers()
      })
    }
    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe(() => {
      this.evaluateState()
    })

    // NgOnDestroy is not working on instances created by a factory, therefore we need this workaround
    this.router.events.pipe(takeUntil(this.cleanUp$)).forEach((event) => {
      if (event instanceof NavigationStart) {
        this.ngOnDestroy()
      }
    })
  }

  private initializeLayers(): void {
    if (!this.mapService.map.getSource(this.sourceId)) {
      this.mapService.map.addSource(this.sourceId, {
        type: 'geojson',
        data: emptyGeoJsonSource as any,
      })
    }

    if (!this.mapService.map.getLayer(this.layerId)) {
      this.mapService.map.addLayer(this.blacklistAreaLayerSpecification)
      this.mapService.map.addLayer(this.blacklistAreaLineLayerSpecification)
      this.mapEventService.registerForMapEvents(this, true)
    }

    this.blacklistAreaService.blacklistAreas$
      .pipe(
        takeUntil(this.cleanUp$),
        skipWhile(() => !this.blacklistAreaService.isDataInitialized),
      )
      .subscribe(() => {
        this.updateLayer()
      })

    this.evaluateState()
  }

  public async reload(): Promise<void> {
    if (this.mapService.isMapReady && this.isLayerVisible && !this.appState.isLoading) {
      await this.blacklistAreaService.loadRawEvents()
    }
  }

  private evaluateState(): void {
    if (this.appState.selectLayers().some((layer) => layer === this.layer)) {
      this.setVisibility(true)
    } else {
      this.setVisibility(false)
    }

    // if layer is not visible stop all events
    if (this.isLayerVisible === false) return

    if (!this.blacklistAreaService.isDataInitialized) {
      this.reload()
      return
    }

    if (
      this.appState.hasLayerVisibilityChanged(Layers.BlacklistAreas.name) &&
      !this.appState.getState().historyEnabled
    ) {
      this.reload()
    }
  }

  public updateLayer(): void {
    const source = this.mapService.map.getSource(this.sourceId) as GeoJSONSource
    if (!source || !('setData' in source)) return

    const featureCollection = this.blacklistAreaService.getAllBlacklistAreas()

    source.setData(featureCollection as any)
  }

  setVisibility(isVisible: boolean): void {
    this.isLayerVisible = isVisible
    if (this.mapService.isMapReady) {
      if (this.mapService.getLayerVisibilty(this.layer) != this.isLayerVisible) {
        this.mapService.setLayerVisibility(this.layerId, isVisible)
        this.mapService.setLayerVisibility(this.layerLineId, isVisible)
      }
    }
  }

  ngOnDestroy(): void {
    this.cleanUp$.next()
    this.cleanUp$.complete()

    this.mapEventService.detachMapEvents(this)

    if (this.mapService.map.getLayer(this.layerLineId)) {
      this.mapService.map.removeLayer(this.layerLineId)
    }
    if (this.mapService.map.getLayer(this.layerId)) {
      this.mapService.map.removeLayer(this.layerId)
    }
    if (this.mapService.map.getSource(this.sourceId)) {
      this.mapService.map.removeSource(this.sourceId)
    }
  }
}
